import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Stack,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAxiosClient } from '../utils/useAxiosClient';
import { useDevAuth } from '../utils/useDevAuth';
import PeopleIcon from '@mui/icons-material/People';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';

interface Trial {
  accessId: number;
  userId: number;
  userEmail: string | null;
  organizationId: number;
  organizationName: string | null;
  role: string;
  isTrial: boolean;
  trialStartDate: string | null;
  trialEndDate: string | null;
  isActive: boolean;
  daysRemaining: number | null;
  expired: boolean;
  status: 'active' | 'expired' | 'revoked';
  lastAccessDate: string | null;
  createdAt: string | null;
}

interface UserAnalytics {
  userId: number;
  userEmail: string;
  days: number;
  totals: {
    events: number;
    sessions: number;
    totalMinutes: number;
    firstSeen: string | null;
    lastSeen: string | null;
  };
  pages: { path: string; views: number; totalMinutes: number }[];
  clicks: { target: string; count: number }[];
  sessions: { sessionId: string; started: string | null; ended: string | null; events: number; totalMinutes: number }[];
}

interface TrialConfig {
  canManageTrials: boolean;
  organizations: { organizationId: number; organizationName: string }[];
  allowedRoles: string[];
  allowedDurations: number[];
  defaultRole: string;
  defaultDurationDays: number;
}

const fmtDate = (iso: string | null): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

// Human-readable remaining-time label for the status chip.
const remainingLabel = (t: Trial): string => {
  if (t.status === 'revoked') return 'revoked';
  if (t.daysRemaining === null) return t.status;
  if (t.daysRemaining < 0) return `expired ${Math.abs(t.daysRemaining)}d ago`;
  if (t.daysRemaining === 0) return 'ends today';
  return `${t.daysRemaining}d left`;
};

const statusColor = (t: Trial): 'success' | 'error' | 'default' | 'warning' => {
  if (t.status === 'revoked') return 'default';
  if (t.status === 'expired') return 'error';
  if (t.daysRemaining !== null && t.daysRemaining <= 2) return 'warning';
  return 'success';
};

/**
 * Admin Users - trial user management.
 *
 * Two permission tiers are in play here. Reaching /admin at all requires the
 * general admin gate (any @crant.ai address). Actually managing trials requires
 * users.super_admin, which the backend reports via /api/admin/trials/config.
 * The backend enforces this independently on every trial endpoint; the checks
 * below only decide what to render.
 */
const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const axiosClient = useAxiosClient();
  const { user } = useDevAuth();

  const [config, setConfig] = useState<TrialConfig | null>(null);
  const [trials, setTrials] = useState<Trial[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
    { open: false, message: '', severity: 'success' }
  );

  // Grant dialog
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantOrg, setGrantOrg] = useState<number | ''>('');
  const [grantRole, setGrantRole] = useState('');
  const [grantDays, setGrantDays] = useState<number>(7);
  const [saving, setSaving] = useState(false);

  // Edit dialog
  const [editTrial, setEditTrial] = useState<Trial | null>(null);
  const [editDays, setEditDays] = useState<number>(7);
  const [editRole, setEditRole] = useState('');

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<Trial | null>(null);

  // Activity drill-down
  const [activityFor, setActivityFor] = useState<Trial | null>(null);
  const [activity, setActivity] = useState<UserAnalytics | null>(null);
  const [activityLoading, setActivityLoading] = useState(false);

  const showSnackbar = (message: string, severity: 'success' | 'error' = 'success') =>
    setSnackbar({ open: true, message, severity });

  const errorText = (e: any, fallback: string) =>
    e?.response?.data?.detail?.error || e?.response?.data?.error || fallback;

  // General admin gate, mirroring the backend's get_admin_auth_payload check.
  const provider = user?.sub?.split('|')[0];
  const emailLower = user?.email?.toLowerCase() || '';
  const isAdmin = (provider === 'google-oauth2' || provider === 'clerk') && (emailLower === 'weezilla@gmail.com' || emailLower.endsWith('@crant.ai'));

  const loadTrials = useCallback(async () => {
    try {
      const res = await axiosClient.get('/api/admin/trials');
      setTrials(res.data.trials || []);
    } catch (e: any) {
      showSnackbar(errorText(e, 'Failed to load trials'), 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [axiosClient]);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosClient.get('/api/admin/trials/config');
        if (cancelled) return;
        const cfg: TrialConfig = res.data;
        setConfig(cfg);
        setGrantRole(cfg.defaultRole);
        setGrantDays(cfg.defaultDurationDays);
        if (cfg.organizations.length === 1) setGrantOrg(cfg.organizations[0].organizationId);
        if (cfg.canManageTrials) await loadTrials();
      } catch (e: any) {
        if (!cancelled) showSnackbar(errorText(e, 'Failed to load trial config'), 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, axiosClient]);

  const handleGrant = async () => {
    if (!grantEmail.trim() || !grantOrg) return;
    setSaving(true);
    try {
      await axiosClient.post('/api/admin/trials', {
        userEmail: grantEmail.trim(),
        organizationId: grantOrg,
        role: grantRole,
        durationDays: grantDays,
      });
      await loadTrials();
      setGrantOpen(false);
      setGrantEmail('');
      showSnackbar(`Trial granted to ${grantEmail.trim()}`);
    } catch (e: any) {
      showSnackbar(errorText(e, 'Failed to grant trial'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSave = async () => {
    if (!editTrial) return;
    setSaving(true);
    try {
      await axiosClient.put(`/api/admin/trials/${editTrial.accessId}`, {
        durationDays: editDays,
        role: editRole,
        isActive: true,
      });
      await loadTrials();
      setEditTrial(null);
      showSnackbar(`Trial extended to ${editDays} days from now`);
    } catch (e: any) {
      showSnackbar(errorText(e, 'Failed to update trial'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (t: Trial) => {
    try {
      await axiosClient.put(`/api/admin/trials/${t.accessId}`, { isActive: !t.isActive });
      await loadTrials();
      showSnackbar(t.isActive ? 'Trial revoked' : 'Trial restored');
    } catch (e: any) {
      showSnackbar(errorText(e, 'Failed to update trial'), 'error');
    }
  };

  const openActivity = async (t: Trial) => {
    setActivityFor(t);
    setActivity(null);
    setActivityLoading(true);
    try {
      const res = await axiosClient.get(`/api/admin/analytics/user/${t.userId}`, { params: { days: 90 } });
      setActivity(res.data);
    } catch (e: any) {
      showSnackbar(errorText(e, 'Failed to load activity'), 'error');
    } finally {
      setActivityLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axiosClient.delete(`/api/admin/trials/${deleteTarget.accessId}`);
      setTrials(prev => prev.filter(x => x.accessId !== deleteTarget.accessId));
      showSnackbar('Trial deleted');
    } catch (e: any) {
      showSnackbar(errorText(e, 'Failed to delete trial'), 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4">Unauthorized</Typography>
        <Typography>You do not have access to this page.</Typography>
      </Box>
    );
  }

  const cellSx = { fontSize: '0.85rem' };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Button variant="text" onClick={() => navigate('/admin')} sx={{ mb: 2 }}>
        &larr; Back to Admin Dashboard
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Trial Users
        </Typography>
        {config?.canManageTrials && (
          <Button variant="contained" onClick={() => setGrantOpen(true)}>
            + New Trial
          </Button>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Super-admin gate: plain admins see this instead of the manager. */}
      {!loading && config && !config.canManageTrials && (
        <Paper sx={{ p: 6, textAlign: 'center', bgcolor: '#f5f5f5', border: '2px dashed #ccc' }}>
          <LockIcon sx={{ fontSize: 72, color: '#9e9e9e', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 2, color: 'text.secondary' }}>
            Restricted
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520, margin: '0 auto' }}>
            Trial management is limited to super admins. Ask Alvaro or Michael if you need a trial
            granted or extended.
          </Typography>
        </Paper>
      )}

      {!loading && config?.canManageTrials && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Trials expire automatically &mdash; once the end date passes, the user is blocked at the
            access check on their next request. Extending re-bases the end date from now.
          </Typography>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Organization</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Role</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Started</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Ends</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }}>Last access</TableCell>
                  <TableCell sx={{ ...cellSx, fontWeight: 600 }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trials.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ ...cellSx, textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      <PeopleIcon sx={{ fontSize: 48, color: '#bdbdbd', display: 'block', margin: '0 auto 8px' }} />
                      No trials yet. Use &ldquo;New Trial&rdquo; to grant one.
                    </TableCell>
                  </TableRow>
                )}
                {trials.map(t => (
                  <TableRow key={t.accessId} hover>
                    <TableCell sx={{ ...cellSx, fontWeight: 500 }}>{t.userEmail || '(unknown)'}</TableCell>
                    <TableCell sx={cellSx}>{t.organizationName || t.organizationId}</TableCell>
                    <TableCell sx={cellSx}>{t.role}</TableCell>
                    <TableCell sx={cellSx}>{fmtDate(t.trialStartDate)}</TableCell>
                    <TableCell sx={cellSx}>{fmtDate(t.trialEndDate)}</TableCell>
                    <TableCell sx={cellSx}>
                      <Chip size="small" label={remainingLabel(t)} color={statusColor(t)} />
                    </TableCell>
                    <TableCell sx={cellSx}>{fmtDate(t.lastAccessDate)}</TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button size="small" onClick={() => openActivity(t)}>
                          Activity
                        </Button>
                        <Button
                          size="small"
                          onClick={() => {
                            setEditTrial(t);
                            setEditDays(config.defaultDurationDays);
                            setEditRole(t.role);
                          }}
                        >
                          Extend
                        </Button>
                        <Button size="small" color={t.isActive ? 'warning' : 'success'} onClick={() => handleToggleActive(t)}>
                          {t.isActive ? 'Revoke' : 'Restore'}
                        </Button>
                        <Tooltip arrow title="Delete trial row">
                          <IconButton size="small" onClick={() => setDeleteTarget(t)}>
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Grant dialog */}
      <Dialog open={grantOpen} onClose={() => setGrantOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Trial</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email address"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              fullWidth
              autoFocus
              helperText="If this person has never logged in, the account is created and claimed on their first login."
            />
            <FormControl fullWidth>
              <InputLabel>Organization</InputLabel>
              <Select
                label="Organization"
                value={grantOrg}
                onChange={(e) => setGrantOrg(Number(e.target.value))}
              >
                {config?.organizations.map(o => (
                  <MenuItem key={o.organizationId} value={o.organizationId}>
                    {o.organizationName} ({o.organizationId})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={grantRole} onChange={(e) => setGrantRole(String(e.target.value))}>
                {config?.allowedRoles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Duration</InputLabel>
              <Select label="Duration" value={grantDays} onChange={(e) => setGrantDays(Number(e.target.value))}>
                {config?.allowedDurations.map(d => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGrantOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleGrant} disabled={saving || !grantEmail.trim() || !grantOrg}>
            {saving ? 'Granting...' : 'Grant Trial'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Extend dialog */}
      <Dialog open={!!editTrial} onClose={() => setEditTrial(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Extend Trial</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {editTrial?.userEmail} &mdash; {editTrial?.organizationName}
          </DialogContentText>
          <Stack spacing={2}>
            <FormControl fullWidth>
              <InputLabel>New duration (from now)</InputLabel>
              <Select
                label="New duration (from now)"
                value={editDays}
                onChange={(e) => setEditDays(Number(e.target.value))}
              >
                {config?.allowedDurations.map(d => <MenuItem key={d} value={d}>{d} days</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select label="Role" value={editRole} onChange={(e) => setEditRole(String(e.target.value))}>
                {config?.allowedRoles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTrial(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activity drill-down */}
      <Dialog open={!!activityFor} onClose={() => setActivityFor(null)} maxWidth="md" fullWidth>
        <DialogTitle>Activity &mdash; {activityFor?.userEmail}</DialogTitle>
        <DialogContent dividers>
          {activityLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          )}
          {!activityLoading && activity && activity.totals.events === 0 && (
            <DialogContentText>
              No recorded activity in the last {activity.days} days. Either they haven&rsquo;t logged
              in, or they last visited before analytics was turned on.
            </DialogContentText>
          )}
          {!activityLoading && activity && activity.totals.events > 0 && (
            <Stack spacing={3}>
              <Stack direction="row" spacing={3} flexWrap="wrap">
                {[
                  ['Sessions', activity.totals.sessions],
                  ['Events', activity.totals.events],
                  ['Time on site', `${activity.totals.totalMinutes} min`],
                  ['First seen', fmtDate(activity.totals.firstSeen)],
                  ['Last seen', fmtDate(activity.totals.lastSeen)],
                ].map(([label, value]) => (
                  <Box key={String(label)}>
                    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                    <Typography variant="h6">{value}</Typography>
                  </Box>
                ))}
              </Stack>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Most-visited pages</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellSx}>Path</TableCell>
                      <TableCell sx={cellSx} align="right">Views</TableCell>
                      <TableCell sx={cellSx} align="right">Minutes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activity.pages.length === 0 && (
                      <TableRow><TableCell colSpan={3} sx={cellSx}>none</TableCell></TableRow>
                    )}
                    {activity.pages.map(p => (
                      <TableRow key={p.path}>
                        <TableCell sx={cellSx}>{p.path}</TableCell>
                        <TableCell sx={cellSx} align="right">{p.views}</TableCell>
                        <TableCell sx={cellSx} align="right">{p.totalMinutes}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Most-clicked</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={cellSx}>Element</TableCell>
                      <TableCell sx={cellSx} align="right">Clicks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activity.clicks.length === 0 && (
                      <TableRow><TableCell colSpan={2} sx={cellSx}>none</TableCell></TableRow>
                    )}
                    {activity.clicks.map(c => (
                      <TableRow key={c.target}>
                        <TableCell sx={cellSx}>{c.target}</TableCell>
                        <TableCell sx={cellSx} align="right">{c.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivityFor(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete trial?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This permanently removes the trial access row for {deleteTarget?.userEmail}. To keep the
            record but block access, use Revoke instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsers;
